// Issue #690 — treasurer copy for "what did this amendment change?".
//
// An amendment is a complete replacement filing: the FEC never asks what moved, so the format
// carries no record of it and the screen is the only place the question can be answered. It
// matters most at the moment it is least recoverable — immediately before upload, when the
// treasurer's real question is "did this change what I meant it to change, and nothing else?".
//
// The wire payload carries facts only (`AmendmentDiffDto`); every sentence lives here so the two
// report surfaces read identically and no enum identifier leaks into treasurer copy.
import type { AmendmentDiffCategory, AmendmentDiffChange } from '../generated/enums.gen';
import { assertNever } from '../utils/assertNever';

/** The schedule a diff section covers, in the words a treasurer uses for it. */
export function amendmentDiffCategoryLabel(category: AmendmentDiffCategory): string {
  switch (category) {
    case 'Contributions':
      return 'Money in';
    case 'Expenditures':
      return 'Money out';
    case 'Refunds':
      return 'Refunds issued';
    default:
      return assertNever(category);
  }
}

/** What happened to one row, as a short badge. */
export function amendmentDiffChangeLabel(change: AmendmentDiffChange): string {
  switch (change) {
    case 'Added':
      return 'Added';
    case 'Altered':
      return 'Changed';
    case 'Removed':
      return 'Removed';
    default:
      return assertNever(change);
  }
}

export const AmendmentDiffCopy = {
  /** Panel heading. */
  Heading: 'What this amendment changes',

  /**
   * The one-line statement of what the reader is looking at. `correcting` names the report this
   * amendment replaces, phrased so it reads the same whether the predecessor was the original
   * filing or an earlier amendment.
   */
  correcting(baselineName: string | null, baselineAmendmentNumber: number | null): string {
    const target = baselineName ?? 'the earlier filing';
    if (baselineAmendmentNumber != null && baselineAmendmentNumber > 0) {
      return `This is a correction. It replaces amendment #${baselineAmendmentNumber} of ${target}.`;
    }
    return `This is a correction. It replaces the original filing of ${target}.`;
  },

  /**
   * Shown in place of the comparison when the report being corrected left no frozen record of
   * what it filed. Says what we do not know rather than inventing a difference — with no baseline
   * every row would otherwise read as newly added, which would be wrong about a filing that in
   * fact carried them.
   */
  NoSnapshot:
    'We have no saved copy of what the earlier filing contained, so we can’t show what changed. '
    + 'Everything below is what this amendment will file — check it against your own copy of the '
    + 'earlier report before you upload.',

  /**
   * Sits under the heading, above the sections. Names the axis of comparison, because the frozen
   * as-filed record keeps only each row's amount and date — see `NothingChanged`.
   */
  Subhead: 'Compared with what was actually filed — amounts and dates.',

  /**
   * Shown when the comparison ran and found nothing different. Scoped deliberately: the frozen
   * record of a filing keeps each row's amount and date and nothing else, so a correction to a
   * donor's employer, occupation or address — one of the commonest reasons to amend — is invisible
   * here. Claiming "nothing differs" would be a false reassurance at exactly the moment the
   * treasurer is asking "did this change what I meant it to change, and nothing else?".
   */
  NothingChanged:
    'No amounts or dates differ from the report it corrects. A correction to someone’s details — '
    + 'employer, occupation, address — won’t show here: the saved copy of a filing keeps only the '
    + 'figures.',

  /** The per-section "and the rest are untouched" note. */
  unchanged(count: number): string {
    return count === 1 ? '1 row unchanged' : `${count} rows unchanged`;
  },

  /** Column headers for the row table. */
  AsFiledHeader: 'As filed',
  AmendedHeader: 'On this amendment',

  /** Stands in for a row whose counterparty record was deleted after the earlier filing. */
  UnknownCounterparty: 'Deleted record',

  /** The row-detail disclosure, in its two states. */
  HideDetail: 'Hide detail',
  ShowDetail: 'Show detail',

  /** Appended to the correcting-statement when the report being corrected has a filing date. */
  filedOn(date: string): string {
    return `Filed ${date}.`;
  },
} as const;
